// Import auth utilities
import { checkResponseStatus } from '../utils/authUtils';

// Helper function to get token
const getAuthToken = () => {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
};

export async function fetchUpcomingInstallments() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const url = `${API_BASE_URL}api/v1/dashboard/insight/upcoming`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  let data;
  try {
    data = await response.json();
  } catch (e) {
    const errorText = await response.text();
    throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
  }

  if (!response.ok) {
    const errorMessage = data?.message || `Server error: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }
  return data;
}

export async function fetchExpectedInsight(range = '12m', dateRange = null) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }
  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }
  
  // Build query parameters
  let queryParams = '';
  if (dateRange && dateRange.start_date && dateRange.end_date) {
    queryParams = `?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
  } else if (range) {
    queryParams = `?range=${range}`;
  }
  
  const url = `${API_BASE_URL}api/v1/dashboard/insight/expected${queryParams}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  let data;
  try {
    data = await response.json();
  } catch (e) {
    const errorText = await response.text();
    throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
  }
  if (!response.ok) {
    const errorMessage = data?.message || `Server error: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }
  return data;
}

export async function fetchRealInsight(range = '7d', dateRange = null) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }
  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }
  
  // Build query parameters
  let queryParams = '';
  if (dateRange && dateRange.start_date && dateRange.end_date) {
    queryParams = `?start_date=${dateRange.start_date}&end_date=${dateRange.end_date}`;
  } else if (range) {
    queryParams = `?range=${range}`;
  }
  
  const url = `${API_BASE_URL}api/v1/dashboard/insight/real${queryParams}`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  let data;
  try {
    data = await response.json();
  } catch (e) {
    const errorText = await response.text();
    throw new Error(errorText || `Server error: ${response.status} ${response.statusText}`);
  }
  if (!response.ok) {
    const errorMessage = data?.message || `Server error: ${response.status} ${response.statusText}`;
    throw new Error(errorMessage);
  }
  return data;
}

export async function exportInsights() {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  if (!API_BASE_URL) {
    throw new Error('REACT_APP_API_BASE_URL environment variable is not set');
  }

  const accessToken = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
  };
  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
    headers['Cookie'] = `jwt=${accessToken}`;
  }

  const url = `${API_BASE_URL}api/v1/dashboard/insight/export-quarter`;
  const response = await fetch(url, {
    method: 'GET',
    headers,
  });

  // Check for 401 status
  checkResponseStatus(response);

  if (!response.ok) {
    let errorMessage = 'Failed to export insights';
    try {
      const errorData = await response.json();
      errorMessage = errorData?.message || errorMessage;
    } catch (e) {
      const errorText = await response.text();
      console.error('Non-JSON response:', errorText);
      errorMessage = `Server error: ${response.status} ${response.statusText}`;
    }
    throw new Error(errorMessage);
  }

  // Get the blob data for CSV download
  const blob = await response.blob();

  // Create download link
  const downloadUrl = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = downloadUrl;

  // Generate filename with current date
  const currentDate = new Date().toISOString().split('T')[0];
  link.download = `insights-export-${currentDate}.csv`;

  // Trigger download
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the URL object
  window.URL.revokeObjectURL(downloadUrl);

  return { success: true, message: 'Insights exported successfully' };
}